import {
  checkNextActionInFlight,
  type CustomerInteractionActivity,
  type CustomerInteractionCallActivity,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import { IconFileText } from "@tabler/icons-react";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";
import { callFailureReasonLabel } from "./callActivityColumns.js";

/**
 * Why an interaction a voice agent calls into has nothing to show yet. Without
 * this the overview and transcript panels read as broken, when in fact the
 * call simply hasn't happened — or happened in a way that produced no audio.
 */
export interface CallActivityEmptyStateProps {
  callActivity: CustomerInteractionCallActivity | null | undefined;
  surface: "overview" | "transcript";
}

export const CallActivityEmptyState: FC<CallActivityEmptyStateProps> = (
  props,
) => {
  const { callActivity, surface } = props;

  const copy = resolveCopy(callActivity, surface);
  if (!copy) {
    return null;
  }

  return (
    <section
      className={twMerge(
        "flex items-center justify-center px-6",
        // On the overview it is one block among sections, so it carries its own
        // card. In the transcript panel it stands in for the whole transcript,
        // so it fills the space the transcript would have taken instead.
        surface === "overview"
          ? "min-h-56 rounded-lg border border-border-light bg-card py-10"
          : "min-h-0 flex-1 py-12",
      )}
    >
      <div className="flex max-w-sm flex-col items-center text-center">
        <IconFileText aria-hidden className="size-8 text-slate-300" />
        <h3 className="mt-3 text-sm font-semibold text-foreground">
          {copy.heading}
        </h3>
        <p className="mt-1 text-sm leading-6 text-muted-foreground">
          {copy.description}
        </p>
      </div>
    </section>
  );
};

interface EmptyStateCopy {
  heading: string;
  description: string;
}

function resolveCopy(
  callActivity: CustomerInteractionCallActivity | null | undefined,
  surface: CallActivityEmptyStateProps["surface"],
): EmptyStateCopy | null {
  if (!callActivity) {
    return null;
  }

  const isTranscript = surface === "transcript";
  // A call in flight is told by its history ("This call is in progress"), so
  // only a call still ahead counts as a next action here.
  const nextAction =
    callActivity.next_action &&
    !checkNextActionInFlight(callActivity.next_action)
      ? callActivity.next_action
      : null;
  const hasNextAction = nextAction !== null;
  const mostRecent = callActivity.history.at(0);

  if (nextAction?.type === "monitoring_paused") {
    return {
      heading: isTranscript ? "No transcript yet" : "No analysis yet",
      description:
        "Outbound outreach is off for this agent. Re-enable it to resume this outreach.",
    };
  }

  // Nothing has happened yet, so the only story is the call ahead.
  if (!mostRecent) {
    if (!nextAction) {
      return null;
    }

    return {
      heading: isTranscript ? "No transcript yet" : "No analysis yet",
      description:
        nextAction.type === "paused"
          ? "Outbound calling is off for this agent, so the scheduled call will not be placed until it is re-enabled."
          : nextAction.type === "monitoring"
            ? "The agent is monitoring this outreach. Calls and texts appear here as they happen."
            : "This call hasn't happened yet. It will appear here after the call.",
    };
  }

  // The agent is still at it, so an unanswered call is not the end of the story.
  if (nextAction?.type === "monitoring" && mostRecent.type !== "call_started") {
    return {
      heading: isTranscript ? "No transcript yet" : "No analysis yet",
      description:
        "The agent is monitoring this outreach. Calls and texts appear here as they happen.",
    };
  }

  const description = describeActivity(mostRecent, hasNextAction);
  if (!description) {
    return null;
  }

  return {
    heading: isTranscript ? "No transcript available" : "No analysis available",
    description,
  };
}

/**
 * The heading already says which panel is empty, so these say only why — no
 * "a transcript" / "analysis" threading.
 */
function describeActivity(
  activity: CustomerInteractionActivity,
  hasNextAction: boolean,
): string | null {
  switch (activity.type) {
    case "call_started":
      return "This call is in progress. It will appear here after it ends.";
    case "call_not_answered":
      if (hasNextAction) {
        return "The most recent call was not answered. Another attempt is scheduled.";
      }

      // A task that gives up stamps the reason onto the call that turned out to
      // be its last, so this is where "the agent has stopped trying" has to be
      // said — otherwise a contact who was never reached looks merely idle.
      return activity.error_code
        ? `The most recent call was not answered, and no further attempts are scheduled — ${
            callFailureReasonLabel[activity.error_code] ?? "the call task ended"
          }.`
        : "The most recent call was not answered.";
    case "call_canceled":
      return "This call was canceled before it happened, so there is nothing to show.";
    case "call_forwarded":
      return "This call was forwarded before the agent handled it, so we have no recording of it.";
    case "call_failed":
      return activity.error_code
        ? `This call could not be placed — ${
            callFailureReasonLabel[activity.error_code] ?? "the call task ended"
          }.`
        : "This call could not be placed.";
    // An answered call has been through the pipeline; an empty panel there is
    // about processing, not about the call, and this component would only
    // explain it away.
    case "call_answered":
      return null;
  }
}
